import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Dialog as Primitive } from 'radix-ui'

import { cn } from '@/lib/cn'

import { useEnterTransition } from './Dialog'
import { IconButton } from './IconButton'

export interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  hideTitle?: boolean
  /** Rendered on the right of the header, e.g. a "Clear" action. */
  headerAction?: ReactNode
  footer?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * A panel that slides up from the bottom on phones and in from the right on
 * desktop — the queue, filters, and other secondary surfaces live here rather
 * than in a centred dialog, which reads wrong at phone widths.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  hideTitle = false,
  headerAction,
  footer,
  children,
  className,
}: SheetProps) {
  const entered = useEnterTransition(open)

  const describedBy: { 'aria-describedby'?: undefined } = description
    ? {}
    : { 'aria-describedby': undefined }

  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      <Primitive.Portal>
        <Primitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-overlay transition-opacity duration-200',
            entered ? 'opacity-100' : 'opacity-0',
          )}
        />
        <Primitive.Content
          {...describedBy}
          className={cn(
            'fixed z-50 flex flex-col border-border bg-surface-raised shadow-2xl',
            'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-2xl border-t',
            'md:inset-y-0 md:left-auto md:right-0 md:h-dvh md:max-h-none md:w-96 md:rounded-none md:border-l md:border-t-0',
            'transition-transform duration-200 ease-out',
            entered
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-full',
            className,
          )}
        >
          {/* Drag affordance: phone users read this as "this panel came from below". */}
          <div className="flex justify-center pt-2 md:hidden" aria-hidden="true">
            <span className="h-1 w-9 rounded-full bg-border-strong" />
          </div>

          <div className="flex items-start gap-2 px-4 pb-3 pt-3">
            <div className="min-w-0 flex-1">
              <Primitive.Title
                className={cn('text-base font-semibold text-text', hideTitle && 'sr-only')}
              >
                {title}
              </Primitive.Title>
              {description ? (
                <Primitive.Description className="mt-0.5 text-sm text-text-muted">
                  {description}
                </Primitive.Description>
              ) : null}
            </div>
            {headerAction}
            <Primitive.Close asChild>
              <IconButton aria-label="Close" size="sm">
                <X className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            </Primitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
            {children}
          </div>

          {footer ? (
            <div className="safe-bottom border-t border-border px-4 py-3">{footer}</div>
          ) : (
            <div className="safe-bottom" />
          )}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  )
}
